import { useCallback, useEffect, useRef, useState } from "react";
import type { DataSource, GridIncomplete, InputParam, SortOrder } from "../types/grid.ts";
import { getDataGrid, getDataGridCount, getDataGridHeader } from "../api/endpoints.ts";
import { parseGridData } from "../util/gridDataParser.ts";
import { gridRequestFilters } from "../util/mergeGridFilters.ts";

interface UseGridDataResult {
  headerSource: DataSource | null;
  headerLoading: boolean;
  /** The header phase has finished for the current grid (fetched, failed, or skipped). */
  headerSettled: boolean;
  dataSource: DataSource | null;
  loading: boolean;
  error: string | null;
  /** The current page arrived incomplete (a database error part-way); see util/incompleteData.ts. */
  incomplete: GridIncomplete | null;
  /** The last search was cancelled by the user. */
  cancelled: boolean;
  /** Stops the running search, and the query behind it. */
  cancel: () => void;
  hasSearched: boolean;
  filters: InputParam[];
  sorts: SortOrder[];
  page: number;
  pageSize: number;
  setFilters: (filters: InputParam[]) => void;
  setSorts: (sorts: SortOrder[]) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  search: () => void;
  refresh: () => void;
  clear: () => void;
  knownTotal: number | null;
  countLoading: boolean;
  /** Why the last count request produced no number (report can't count, timeout, error). */
  countError: string | null;
  fetchCount: () => void;
}

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 250] as const;

const DEFAULT_PAGESIZE_KEY = "webadmincc-default-pagesize";

export function getDefaultPageSize(): number {
  try {
    const raw = localStorage.getItem(DEFAULT_PAGESIZE_KEY);
    if (raw) {
      const n = parseInt(raw, 10);
      if ((PAGE_SIZE_OPTIONS as readonly number[]).includes(n)) return n;
    }
  } catch { /* noop */ }
  return 50;
}

export function setDefaultPageSize(size: number): void {
  localStorage.setItem(DEFAULT_PAGESIZE_KEY, String(size));
}

/** The server's own message: it writes errors for the person reading them (ClientError) —
 *  plain and actionable for everyone, with the details added for super admins. */
function gridErrorMessage(raw?: string | null): string {
  return raw?.trim() || "The data could not be loaded. Try again; if it keeps happening, contact support.";
}

/** The request never got an answer: the network, not the server, failed. */
const NO_ANSWER = "The server could not be reached. Check your connection and try again.";

export function useGridData(
  name: string,
  staticFilters?: InputParam[],
  paramFilters?: InputParam[],
  migratedFilters?: Record<string, string | null> | null,
  skipHeader?: boolean,
  // User-changed column selection (Spec C §6.3); null/undefined = report default.
  // Read from a ref at fetch time — changing it does NOT trigger a refetch by itself
  // (the caller batches that to the Columns… modal close via refresh()).
  columnList?: string[] | null,
  // Skip the GetDataGridHeader fetch entirely because the caller supplies the
  // grid attributes from cache (used for SP grids with defined columns, whose
  // header call would EXEC the whole stored procedure just to read the schema).
  // Unlike `skipHeader`, this does NOT auto-search — data waits for the user /
  // the autoSearch effect once param defaults resolve.
  deferHeaderFetch?: boolean,
): UseGridDataResult {
  const [headerSource, setHeaderSource] = useState<DataSource | null>(null);
  const [headerLoading, setHeaderLoading] = useState(false);
  // headerLoading starts false, so it can't tell "not started" from "done"; autoSearch
  // waits on this instead.
  const [headerSettled, setHeaderSettled] = useState(false);
  const [dataSource, setDataSource] = useState<DataSource | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [incomplete, setIncomplete] = useState<GridIncomplete | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState<InputParam[]>([]);
  const [sorts, setSorts] = useState<SortOrder[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    const cached = localStorage.getItem(`webadmincc-pagesize-${name.toUpperCase()}`);
    if (cached) {
      const n = parseInt(cached, 10);
      if ((PAGE_SIZE_OPTIONS as readonly number[]).includes(n)) return n;
    }
    return getDefaultPageSize();
  });
  const [refreshToken, setRefreshToken] = useState(0);
  const [knownTotal, setKnownTotal] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const countAbortRef = useRef<AbortController | null>(null);
  const headerAbortRef = useRef<AbortController | null>(null);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const staticFiltersRef = useRef(staticFilters);
  staticFiltersRef.current = staticFilters;

  const paramFiltersRef = useRef(paramFilters);
  paramFiltersRef.current = paramFilters;

  const migratedFiltersRef = useRef(migratedFilters);
  migratedFiltersRef.current = migratedFilters;

  const columnListRef = useRef(columnList);
  columnListRef.current = columnList;

  // Persist page size to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(`webadmincc-pagesize-${name.toUpperCase()}`, String(pageSize));
  }, [name, pageSize]);

  // Phase 1: Header fetch on mount / name change
  // When skipHeader is true, skip the separate GetDataGridHeader call —
  // the first GetDataGrid response will provide the header attributes.
  useEffect(() => {
    headerAbortRef.current?.abort();
    const controller = new AbortController();
    headerAbortRef.current = controller;
    let ignore = false;

    setHeaderSource(null);
    setHeaderSettled(false);
    setDataSource(null);
    setHasSearched(false);
    setLoading(false);
    setKnownTotal(null); setCountError(null);
    // Note: don't reset filters/sorts here. The parent (DataGrid) applies
    // persisted filters in its own mount effect; resetting here would race
    // with StrictMode double-invocation and wipe them. Since <DataGrid key={name}>
    // remounts on grid name change, there's no need to reset filters in-place.
    setPage(0);
    {
      const cached = localStorage.getItem(`webadmincc-pagesize-${name.toUpperCase()}`);
      const n = cached ? parseInt(cached, 10) : NaN;
      setPageSize((PAGE_SIZE_OPTIONS as readonly number[]).includes(n) ? n : 25);
    }

    if (skipHeader) {
      // Skip header fetch — the first data response provides attributes.
      // Trigger search immediately so Phase 2 fires.
      setHeaderLoading(false);
      setHeaderSettled(true);
      setHasSearched(true);
      return () => { ignore = true; };
    }

    if (deferHeaderFetch) {
      // Caller supplies attributes from cache (SP grid, defined columns) — skip
      // the header (it would run the whole SP). Do NOT auto-search; the grid
      // renders its columns/filters from the cached attributes and waits.
      setHeaderLoading(false);
      setHeaderSettled(true);
      return () => { ignore = true; };
    }

    setHeaderLoading(true);

    // Delay the fetch slightly so StrictMode's unmount-remount cycle
    // cancels the first invocation before the request is sent.
    const timer = setTimeout(() => {
      if (ignore) return;
      getDataGridHeader(name)
        .then((result) => {
          if (ignore) return;
          if (!result.bError && result.oData) {
            setHeaderSource(result.oData);
          }
        })
        .catch((err: unknown) => {
          if (ignore) return;
          console.error("Grid header fetch error:", err);
        })
        .finally(() => {
          if (!ignore) {
            setHeaderLoading(false);
            setHeaderSettled(true);
          }
        });
    }, 0);

    return () => {
      ignore = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [name, skipHeader, deferHeaderFetch]);

  // Phase 2: Data fetch � filters are read from ref so typing doesn't trigger refetch
  useEffect(() => {
    if (!hasSearched) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setIncomplete(null);
    setCancelled(false);

    const iIndexStart = page * pageSize;
    const iIndexEnd = iIndexStart + pageSize - 1;
    const allFilters = gridRequestFilters(
      staticFiltersRef.current, paramFiltersRef.current, filtersRef.current, migratedFiltersRef.current,
    );

    // Defer the request a tick so StrictMode's mount→unmount→mount cycle clears
    // (and aborts) the first invocation before it reaches the network — mirrors
    // the header fetch above. Without this the data request fired twice in dev
    // (the first aborted client-side, but the server still processed both).
    const timer = setTimeout(() => {
      if (controller.signal.aborted) return;
      getDataGrid(name, allFilters, sorts, iIndexStart, iIndexEnd, columnListRef.current, controller.signal)
        .then((gridResult) => {
          if (controller.signal.aborted) return;
          if (!gridResult.bError && gridResult.oData) {
            parseGridData(gridResult.oData);
            // When header was skipped, use the first data response as the header source
            if (!headerSource && gridResult.oData.attributes) {
              setHeaderSource(gridResult.oData);
            }
            setDataSource(gridResult.oData);
            setIncomplete(gridResult.oData.incomplete ?? null);
            const rowCount = gridResult.oData.matrix.length;
            const serverTotal = gridResult.oData.totalCount;
            if (gridResult.oData.incomplete) {
              // Cut short by a database error (the popup says so): a short page is not the end of
              // the data, and the count, if any, never arrived.
              setKnownTotal(null); setCountError(null);
            } else if (serverTotal > 0) {
              setKnownTotal(serverTotal);
            } else if (rowCount < pageSize) {
              setKnownTotal(page * pageSize + rowCount);
            } else {
              setKnownTotal(null); setCountError(null);
            }
          } else {
            // Server returned an error envelope (e.g. SQL command timeout). Clear the
            // stale grid so old rows aren't mistaken for the new result, and surface a message.
            setDataSource(null);
            setKnownTotal(null); setCountError(null);
            setError(gridErrorMessage(gridResult.sMessage));
          }
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          console.error("Grid data fetch error:", err);
          setDataSource(null);
          setKnownTotal(null); setCountError(null);
          setError(NO_ANSWER);
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        });
    }, 0);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [name, hasSearched, sorts, page, pageSize, refreshToken]);

  const search = useCallback(() => {
    setPage(0);
    setKnownTotal(null); setCountError(null);
    setHasSearched(prev => {
      if (prev) setRefreshToken(t => t + 1);
      return true;
    });
  }, []);

  const refresh = useCallback(() => {
    setRefreshToken((t) => t + 1);
  }, []);

  const clear = useCallback(() => {
    setHasSearched(false);
    setDataSource(null);
    setError(null);
    setIncomplete(null);
    setCancelled(false);
    setFilters([]);
    setSorts([]);
    setPage(0);
    setKnownTotal(null); setCountError(null);
  }, []);

  // Aborting the request is what stops the query: the server cancels it when the request goes
  // away. The same happens on its own when the grid unmounts (navigating away) or the page reloads.
  const cancel = useCallback(() => {
    const running = abortRef.current;
    const counting = countAbortRef.current;
    if (running && !running.signal.aborted) {
      running.abort();
      setLoading(false);
      setDataSource(null);
      setKnownTotal(null); setCountError(null);
      setIncomplete(null);
      setCancelled(true);
    }
    if (counting && !counting.signal.aborted) {
      counting.abort();
      setCountLoading(false);
    }
  }, []);

  useEffect(() => () => countAbortRef.current?.abort(), []);

  const fetchCount = useCallback(() => {
    countAbortRef.current?.abort();
    const controller = new AbortController();
    countAbortRef.current = controller;
    setCountLoading(true);
    setCountError(null);
    const allFilters = gridRequestFilters(
      staticFiltersRef.current, paramFiltersRef.current, filtersRef.current, migratedFiltersRef.current,
    );
    getDataGridCount(name, allFilters, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (!result.bError && result.oData) {
          setKnownTotal(result.oData.totalCount);
        } else if (!result.bError) {
          // The server has no count query for this report (stored-procedure reports).
          setCountError("This report can't be counted");
        } else {
          setCountError(result.sMessage || "Count failed");
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("Count fetch error:", err);
        setCountError("Count failed");
      })
      .finally(() => { if (!controller.signal.aborted) setCountLoading(false); });
  }, [name]);

  const handleSetFilters = useCallback((f: InputParam[]) => {
    setFilters(f);
  }, []);

  // Wrap setters that trigger a data refetch so loading appears in the same
  // render frame — otherwise the useEffect sets loading one frame later and
  // the user sees no feedback.
  const handleSetSorts = useCallback((s: SortOrder[]) => {
    setSorts(s);
    if (hasSearched) setLoading(true);
  }, [hasSearched]);

  const handleSetPage = useCallback((p: number) => {
    setPage(p);
    if (hasSearched) setLoading(true);
  }, [hasSearched]);

  const handleSetPageSize = useCallback((ps: number) => {
    setPageSize(ps);
    setPage(0);
    if (hasSearched) setLoading(true);
  }, [hasSearched]);

  return {
    headerSource,
    headerLoading,
    headerSettled,
    dataSource,
    loading,
    error,
    incomplete,
    cancelled,
    cancel,
    hasSearched,
    filters,
    sorts,
    page,
    pageSize,
    setFilters: handleSetFilters,
    setSorts: handleSetSorts,
    setPage: handleSetPage,
    setPageSize: handleSetPageSize,
    search,
    refresh,
    clear,
    knownTotal,
    countLoading,
    countError,
    fetchCount,
  };
}
